'use client'

// src/provider.tsx
import * as React from "react";
import { usePromise } from "@saas-ui/hooks";
import { jsx } from "react/jsx-runtime";
var { createContext, useState, useContext, useEffect, useCallback } = React;
var createAuthContext = () => {
  return createContext(null);
};
var AuthContext = createAuthContext();
var AuthProvider = ({
  refetchUserOnWindowFocus = true,
  onRestoreAuthState,
  onLoadUser = () => Promise.resolve(null),
  onSignup = () => Promise.resolve(null),
  onLogin = () => Promise.resolve(null),
  onVerifyOtp = () => Promise.resolve(null),
  onLogout = () => Promise.resolve(),
  onAuthStateChange,
  onGetToken,
  onResetPassword,
  onUpdatePassword,
  children
}) => {
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState();
  const [isLoading, setLoading] = useState(true);
  const restoreRef = React.useRef(false);
  const isFetchingRef = React.useRef(false);
  useEffect(() => {
    if (onAuthStateChange) {
      const unsubscribe = onAuthStateChange((user2) => {
        setAuthenticated(!!user2);
      });
      return () => {
        unsubscribe == null ? void 0 : unsubscribe();
      };
    }
  }, []);
  useEffect(() => {
    const restoreState = async () => {
      restoreRef.current = true;
      await (onRestoreAuthState == null ? void 0 : onRestoreAuthState());
      await loadUser();
      restoreRef.current = false;
    };
    if (!restoreRef.current) {
      restoreState();
    }
  }, [onRestoreAuthState]);
  const loadUser = useCallback(async () => {
    try {
      if (isFetchingRef.current) {
        return;
      }
      if ((typeof onGetToken === "undefined" || await onGetToken()) && !isFetchingRef.current) {
        isFetchingRef.current = true;
        const user2 = await onLoadUser();
        if (user2) {
          setUser(user2);
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
          setUser(null);
        }
      }
    } catch {
      setAuthenticated(false);
      setUser(null);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [onLoadUser, onGetToken]);
  useEffect(() => {
    if (!refetchUserOnWindowFocus) {
      return;
    }
    const onWindowFocus = async () => {
      loadUser();
    };
    window.addEventListener("focus", onWindowFocus);
    return () => {
      if (onWindowFocus) {
        window.removeEventListener("focus", onWindowFocus);
      }
    };
  }, [loadUser, refetchUserOnWindowFocus]);
  const signUp = useCallback(
    async (params, options) => {
      const result = await onSignup(params, options);
      loadUser();
      return result;
    },
    [onSignup]
  );
  const logIn = useCallback(
    async (params, options) => {
      const result = await onLogin(params, options);
      loadUser();
      return result;
    },
    [onLogin]
  );
  const logOut = useCallback(
    async (options) => {
      await onLogout(options);
      setUser(null);
      setAuthenticated(false);
    },
    [onLogout]
  );
  const verifyOtp = useCallback(
    async (params, options) => {
      const result = await onVerifyOtp(params, options);
      return result;
    },
    [onVerifyOtp]
  );
  const resetPassword = useCallback(
    async (params, options) => {
      return await (onResetPassword == null ? void 0 : onResetPassword(params, options));
    },
    [onResetPassword]
  );
  const updatePassword = useCallback(
    async (params, options) => {
      return await (onUpdatePassword == null ? void 0 : onUpdatePassword(params, options));
    },
    [onUpdatePassword]
  );
  const getToken = useCallback(async () => {
    return onGetToken == null ? void 0 : onGetToken();
  }, [onGetToken]);
  const value = {
    isAuthenticated,
    isLoggingIn: isAuthenticated && !user,
    isLoading,
    user,
    signUp,
    logIn,
    logOut,
    verifyOtp,
    loadUser,
    getToken,
    resetPassword,
    updatePassword
  };
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
};
var useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error(
      "Auth context missing, did you forget to wrap your app in AuthProvider?"
    );
  }
  return context;
};
var useCurrentUser = () => {
  return useAuth().user;
};
var useLogin = ({ action = "logIn" } = {}) => {
  const auth = useAuth();
  const fn = auth[action] || auth["logIn"];
  return usePromise(fn);
};
var useSignUp = () => {
  const { signUp } = useAuth();
  return usePromise(signUp);
};
var useOtp = () => {
  const { verifyOtp } = useAuth();
  return usePromise(verifyOtp);
};
var useResetPassword = () => {
  const { resetPassword } = useAuth();
  return usePromise(resetPassword);
};
var useUpdatePassword = () => {
  const { updatePassword } = useAuth();
  return usePromise(updatePassword);
};

// src/components/forms/forgot-password-form.tsx
import { Form, FormLayout, Field } from "@saas-ui/forms";

// src/components/login-button.tsx
import { useTheme } from "@chakra-ui/react";
import { SubmitButton } from "@saas-ui/forms";
import { jsx as jsx2 } from "react/jsx-runtime";
var LoginButton = (props) => {
  var _a, _b;
  const theme = useTheme();
  const defaultProps = {
    h: "10",
    variant: "solid",
    colorScheme: "primary",
    ...(_b = (_a = theme.components) == null ? void 0 : _a.LoginButton) == null ? void 0 : _b.defaultProps
  };
  return /* @__PURE__ */ jsx2(SubmitButton, { ...defaultProps, ...props });
};
LoginButton.displayName = "LoginButton";

// src/components/forms/forgot-password-form.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var ForgotPasswordForm = ({
  children,
  ...formProps
}) => {
  return /* @__PURE__ */ jsx3(Form, { defaultValues: { email: "" }, ...formProps, children: /* @__PURE__ */ jsxs(FormLayout, { children: [
    /* @__PURE__ */ jsx3(
      Field,
      {
        name: "email",
        label: "Your email address",
        type: "email",
        rules: { required: true },
        autoComplete: "email"
      }
    ),
    children,
    /* @__PURE__ */ jsx3(LoginButton, { type: "submit", width: "full", children: "Reset password" })
  ] }) });
};
ForgotPasswordForm.displayName = "ForgotPasswordForm";

// src/components/forms/update-password-form.tsx
import * as React2 from "react";
import {
  Form as Form2,
  FormLayout as FormLayout2,
  Field as Field2
} from "@saas-ui/forms";
import { jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var UpdatePasswordForm = ({
  children,
  ...formProps
}) => {
  const formRef = React2.useRef(null);
  const validatePassword = React2.useCallback((confirmPassword) => {
    var _a;
    const password = (_a = formRef.current) == null ? void 0 : _a.getValues("password");
    return confirmPassword === password;
  }, []);
  return /* @__PURE__ */ jsx4(
    Form2,
    {
      defaultValues: { password: "", confirmPassword: "" },
      formRef,
      ...formProps,
      children: /* @__PURE__ */ jsxs2(FormLayout2, { children: [
        /* @__PURE__ */ jsx4(
          Field2,
          {
            name: "password",
            label: "New password",
            type: "password",
            rules: { required: true },
            autoComplete: "current-password"
          }
        ),
        /* @__PURE__ */ jsx4(
          Field2,
          {
            name: "confirmPassword",
            label: "Confirm password",
            type: "password",
            rules: { validate: validatePassword },
            autoComplete: "new-password"
          }
        ),
        children,
        /* @__PURE__ */ jsx4(LoginButton, { type: "submit", width: "full", children: "Update password" })
      ] })
    }
  );
};
UpdatePasswordForm.displayName = "UpdatePasswordForm";

// src/components/forms/magic-link-form.tsx
import { Form as Form3, FormLayout as FormLayout3, Field as Field3 } from "@saas-ui/forms";
import { jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var MagicLinkForm = ({
  children,
  ...formProps
}) => {
  return /* @__PURE__ */ jsx5(Form3, { defaultValues: { email: "" }, ...formProps, children: /* @__PURE__ */ jsxs3(FormLayout3, { children: [
    /* @__PURE__ */ jsx5(
      Field3,
      {
        name: "email",
        label: "Email",
        type: "email",
        rules: { required: true },
        autoComplete: "email"
      }
    ),
    children,
    /* @__PURE__ */ jsx5(LoginButton, { type: "submit", width: "full", children: "Continue with Email" })
  ] }) });
};
MagicLinkForm.displayName = "MagicLinkForm";

// src/components/forms/otp-form.tsx
import {
  Form as Form4,
  FormLayout as FormLayout4,
  PinField
} from "@saas-ui/forms";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var OtpForm = ({
  pinLength = 4,
  defaultValues,
  children,
  ...formProps
}) => {
  return /* @__PURE__ */ jsx6(Form4, { defaultValues: { otp: "" }, ...formProps, children: /* @__PURE__ */ jsxs4(FormLayout4, { children: [
    /* @__PURE__ */ jsx6(
      PinField,
      {
        name: "otp",
        label: "Your verification code",
        help: "You can find your one-time password in the Google Authenticator or Authy app.",
        type: "pin",
        pinLength,
        rules: { required: true }
      }
    ),
    children,
    /* @__PURE__ */ jsx6(LoginButton, { type: "submit", width: "full", children: "Verify" })
  ] }) });
};
OtpForm.displayName = "OtpForm";

// src/components/forms/password-form.tsx
import { Form as Form5, FormLayout as FormLayout5, Field as Field4 } from "@saas-ui/forms";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var PasswordForm = ({
  defaultValues,
  children,
  ...formProps
}) => {
  return /* @__PURE__ */ jsx7(Form5, { defaultValues: { email: "", password: "" }, ...formProps, children: /* @__PURE__ */ jsxs5(FormLayout5, { children: [
    /* @__PURE__ */ jsx7(
      Field4,
      {
        name: "email",
        label: "Email",
        type: "email",
        rules: { required: true },
        autoComplete: "email"
      }
    ),
    /* @__PURE__ */ jsx7(
      Field4,
      {
        name: "password",
        label: "Password",
        type: "password",
        rules: { required: true },
        autoComplete: "current-password"
      }
    ),
    children,
    /* @__PURE__ */ jsx7(LoginButton, { type: "submit", width: "full", children: "Log in" })
  ] }) });
};
PasswordForm.displayName = "PasswordForm";

// src/components/forms/providers.tsx
import { SimpleGrid, Icon } from "@chakra-ui/react";
import { callAllHandlers } from "@chakra-ui/utils";

// src/components/provider-button.tsx
import { Button, useTheme as useTheme2 } from "@chakra-ui/react";
import { jsx as jsx8 } from "react/jsx-runtime";
var ProviderButton = (props) => {
  var _a, _b;
  const { children, ...rest } = props;
  const theme = useTheme2();
  const defaultProps = {
    h: 10,
    variant: "outline",
    ...(_b = (_a = theme.components) == null ? void 0 : _a.ProviderButton) == null ? void 0 : _b.defaultProps
  };
  return /* @__PURE__ */ jsx8(Button, { ...defaultProps, ...rest, children });
};
ProviderButton.displayName = "ProviderButton";

// src/components/forms/providers.tsx
import { jsx as jsx9, jsxs as jsxs6 } from "react/jsx-runtime";
var Providers = ({
  providers,
  onSignIn,
  labelPrefix = "Continue with",
  ...rest
}) => {
  if (!providers) {
    return null;
  }
  return /* @__PURE__ */ jsx9(SimpleGrid, { spacing: 4, ...rest, children: Object.entries(providers).map(([id, provider]) => {
    const { name, icon, color, scopes, ...rest2 } = provider;
    return /* @__PURE__ */ jsxs6(
      ProviderButton,
      {
        color,
        leftIcon: icon && /* @__PURE__ */ jsx9(Icon, { as: icon }),
        ...rest2,
        onClick: callAllHandlers(
          rest2.onClick,
          () => onSignIn(id, { scopes })
        ),
        children: [
          labelPrefix,
          " ",
          name
        ]
      },
      id
    );
  }) });
};
Providers.displayName = "Providers";

// src/components/auth-form.tsx
import {
  chakra,
  omitThemingProps,
  useMultiStyleConfig,
  createStylesContext,
  Divider,
  HStack,
  Text
} from "@chakra-ui/react";
import { cx } from "@chakra-ui/utils";
import { jsx as jsx10, jsxs as jsxs7 } from "react/jsx-runtime";
var [StylesProvider, useStyles] = createStylesContext("SuiAuthForm");
var AuthFormContainer = (props) => {
  const { children } = props;
  const styles = useMultiStyleConfig("SuiAuthForm", props);
  const containerProps = omitThemingProps(props);
  const containerStyles = {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    width: "full",
    ...styles.container
  };
  return /* @__PURE__ */ jsx10(StylesProvider, { value: styles, children: /* @__PURE__ */ jsx10(
    chakra.div,
    {
      __css: containerStyles,
      ...containerProps,
      className: cx("sui-auth-form", props.className),
      children
    }
  ) });
};
AuthFormContainer.displayName = "AuthFormContainer";
var AuthFormDivider = (props) => {
  const { label, ...rest } = props;
  const styles = useStyles();
  return /* @__PURE__ */ jsxs7(HStack, { color: "muted", fontSize: "sm", my: "4", children: [
    /* @__PURE__ */ jsx10(Divider, { ...rest, sx: styles.divider }),
    /* @__PURE__ */ jsx10(Text, { flexShrink: "0", children: label }),
    /* @__PURE__ */ jsx10(Divider, { ...rest, sx: styles.divider })
  ] });
};
var AuthFormTitle = ({
  children,
  ...rest
}) => {
  const styles = useStyles();
  const titleStyles = {
    fontSize: "2xl",
    fontWeight: "bold",
    textAlign: "center",
    mb: 8,
    ...styles.title
  };
  return /* @__PURE__ */ jsx10(
    chakra.h2,
    {
      __css: titleStyles,
      ...rest,
      className: cx("sui-auth-form__title", rest.className),
      children
    }
  );
};
AuthFormTitle.displayName = "AuthFormTitle";

// src/components/auth.tsx
import * as React3 from "react";
import { chakra as chakra2, Link, useControllableState } from "@chakra-ui/react";

// src/components/success.tsx
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from "@chakra-ui/react";
import { jsx as jsx11, jsxs as jsxs8 } from "react/jsx-runtime";
var AuthFormSuccess = ({
  title,
  description,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs8(
    Alert,
    {
      status: "success",
      variant: "subtle",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      ...rest,
      children: [
        /* @__PURE__ */ jsx11(AlertIcon, { boxSize: "40px", mr: 0 }),
        /* @__PURE__ */ jsx11(AlertTitle, { mt: 4, mb: 1, fontSize: "lg", children: title }),
        /* @__PURE__ */ jsx11(AlertDescription, { maxWidth: "sm", fontSize: "md", children: description })
      ]
    }
  );
};
AuthFormSuccess.displayName = "AuthFormSuccess";

// src/components/login-view.tsx
import { Fragment, jsx as jsx12, jsxs as jsxs9 } from "react/jsx-runtime";
var LoginView = (props) => {
  const { title = "Log in", ...rest } = props;
  if (props.type === "password") {
    return /* @__PURE__ */ jsx12(PasswordView, { title, ...rest });
  }
  return /* @__PURE__ */ jsx12(MagicLinkView, { title, ...rest });
};
var SignupView = (props) => {
  const { title = "Sign up", ...rest } = props;
  return /* @__PURE__ */ jsx12(LoginView, { action: "signUp", title, ...rest });
};
SignupView.displayName = "SignupView";
var PasswordView = (props) => {
  const {
    onSuccess,
    onError,
    renderSuccess = () => /* @__PURE__ */ jsx12(
      AuthFormSuccess,
      {
        title: "Success!",
        description: "Check your mailbox to verify your account."
      }
    ),
    action,
    title,
    providers,
    providerLabel,
    dividerLabel,
    footer,
    redirectUrl,
    ...formProps
  } = props;
  const [{ isResolved, data }, submit] = useLogin({ action });
  const handleSubmit = (params) => {
    return submit(params, {
      redirectTo: redirectUrl
    }).then(onSuccess).catch(onError);
  };
  const isSuccess = isResolved && action === "signUp";
  if (isResolved && action === "signUp") {
    return renderSuccess(data);
  }
  const wrapperProps = {
    providers,
    title,
    providerLabel,
    dividerLabel,
    footer,
    redirectUrl
  };
  return /* @__PURE__ */ jsx12(AuthFormWrapper, { ...wrapperProps, children: isSuccess ? renderSuccess(data) : /* @__PURE__ */ jsx12(PasswordForm, { onSubmit: handleSubmit, ...formProps }) });
};
var MagicLinkView = (props) => {
  const {
    onSuccess,
    onError,
    renderSuccess = (data2) => /* @__PURE__ */ jsx12(
      AuthFormSuccess,
      {
        title: "Check your mailbox",
        description: `We've sent a magic link to ${(data2 == null ? void 0 : data2.email) || "your email address"}`
      }
    ),
    title,
    action,
    providers,
    providerLabel,
    dividerLabel,
    footer,
    redirectUrl,
    ...formProps
  } = props;
  const [{ isResolved, data }, submit] = useLogin({
    action
  });
  const handleSubmit = ({ email }) => {
    return submit(
      { email },
      {
        redirectTo: redirectUrl
      }
    ).then(onSuccess).catch(onError);
  };
  const wrapperProps = {
    providers,
    title,
    providerLabel,
    dividerLabel,
    footer,
    redirectUrl
  };
  return /* @__PURE__ */ jsx12(AuthFormWrapper, { ...wrapperProps, children: isResolved ? renderSuccess(data) : /* @__PURE__ */ jsx12(MagicLinkForm, { ...formProps, onSubmit: handleSubmit }) });
};
LoginView.displayName = "LoginView";
var AuthFormWrapper = (props) => {
  const {
    providers,
    title,
    providerLabel = "Continue with",
    dividerLabel = "or continue with",
    footer,
    children,
    redirectUrl,
    ...rest
  } = props;
  const { logIn } = useAuth();
  const signInWith = (provider) => {
    return logIn({ provider }, { redirectTo: redirectUrl });
  };
  return /* @__PURE__ */ jsxs9(AuthFormContainer, { ...rest, children: [
    typeof title === "string" ? /* @__PURE__ */ jsx12(AuthFormTitle, { children: title }) : title,
    providers && /* @__PURE__ */ jsxs9(Fragment, { children: [
      /* @__PURE__ */ jsx12(
        Providers,
        {
          providers,
          labelPrefix: providerLabel,
          onSignIn: signInWith
        }
      ),
      /* @__PURE__ */ jsx12(AuthFormDivider, { label: dividerLabel })
    ] }),
    children,
    footer
  ] });
};
AuthFormWrapper.displayName = "AuthForm";

// src/components/forgot-password-view.tsx
import { jsx as jsx13, jsxs as jsxs10 } from "react/jsx-runtime";
var ForgotPasswordView = (props) => {
  const {
    title = "Forgot password",
    footer,
    onSuccess,
    onError,
    onValidationError,
    renderSuccess = () => /* @__PURE__ */ jsx13(
      AuthFormSuccess,
      {
        title: "Password reset requested",
        description: "Please check your email for instructions to reset your password."
      }
    ),
    redirectUrl,
    ...rest
  } = props;
  const [{ data, isResolved }, submit] = useResetPassword();
  const handleSubmit = (params) => {
    return submit(params, {
      redirectTo: redirectUrl
    }).then(onSuccess).catch(onError);
  };
  return /* @__PURE__ */ jsxs10(AuthFormContainer, { children: [
    typeof title === "string" ? /* @__PURE__ */ jsx13(AuthFormTitle, { children: title }) : title,
    isResolved ? renderSuccess(data) : /* @__PURE__ */ jsx13(
      ForgotPasswordForm,
      {
        ...rest,
        onError: onValidationError,
        onSubmit: handleSubmit
      }
    ),
    footer
  ] });
};
ForgotPasswordView.displayName = "ForgotPasswordView";

// src/components/update-password-view.tsx
import { jsx as jsx14, jsxs as jsxs11 } from "react/jsx-runtime";
var UpdatePasswordView = (props) => {
  const {
    title = "Choose a new password",
    footer,
    onSuccess,
    onError,
    onValidationError,
    renderSuccess = () => /* @__PURE__ */ jsx14(
      AuthFormSuccess,
      {
        title: "Your password has been updated",
        description: "You can now log in with your new password."
      }
    ),
    ...rest
  } = props;
  const [{ data, isResolved }, submit] = useUpdatePassword();
  const handleSubmit = ({
    password
  }) => {
    return submit({ password }).then(onSuccess).catch(onError);
  };
  const isSuccess = isResolved && data;
  return /* @__PURE__ */ jsxs11(AuthFormContainer, { children: [
    typeof title === "string" ? /* @__PURE__ */ jsx14(AuthFormTitle, { children: title }) : title,
    isSuccess ? renderSuccess(data) : /* @__PURE__ */ jsx14(
      UpdatePasswordForm,
      {
        ...rest,
        onError: onValidationError,
        onSubmit: handleSubmit
      }
    ),
    footer
  ] });
};
UpdatePasswordView.displayName = "UpdatePasswordView";

// src/components/otp-view.tsx
import { jsx as jsx15, jsxs as jsxs12 } from "react/jsx-runtime";
var OtpView = (props) => {
  const {
    title = "One-time password",
    footer,
    onSuccess,
    onError,
    onValidationError,
    renderSuccess = () => /* @__PURE__ */ jsx15(
      AuthFormSuccess,
      {
        title: "Verification succesful",
        description: "You are now logged in."
      }
    ),
    ...rest
  } = props;
  const [{ data }, submit] = useOtp();
  const handleSubmit = async (params) => {
    return submit(params).then(onSuccess).catch(onError);
  };
  const form = /* @__PURE__ */ jsx15(OtpForm, { ...rest, onError: onValidationError, onSubmit: handleSubmit });
  return /* @__PURE__ */ jsxs12(AuthFormContainer, { children: [
    typeof title === "string" ? /* @__PURE__ */ jsx15(AuthFormTitle, { children: title }) : title,
    data ? renderSuccess(data) : form,
    footer
  ] });
};
OtpView.displayName = "OtpView";

// src/components/auth.tsx
import { jsx as jsx16, jsxs as jsxs13 } from "react/jsx-runtime";
var VIEWS = {
  LOGIN: "login",
  SIGNUP: "signup",
  FORGOT_PASSWORD: "forgot_password",
  UPDATE_PASSWORD: "update_password",
  OTP: "otp"
};
var defaultTranslations = {
  signup: "Sign up",
  signupSubmit: "Sign up",
  signupSuccess: "Success!",
  signupSuccessDescription: "Check your mailbox to verify your account.",
  login: "Log in",
  loginSubmit: "Log in",
  magicLinkSuccess: "Check your mailbox",
  magicLinkSuccessDescription: "We have sent a magic link to {email}.",
  yourEmail: "your email address",
  forgotPassword: "Forgot password?",
  forgotPasswordSubmit: "Send reset link",
  forgotPasswordSuccess: "Password reset requested",
  forgotPasswordSuccessDescription: "Please check your email for instructions to reset your password.",
  updatePassword: "Update password",
  updatePasswordSubmit: "Update password",
  updatePasswordSuccess: "Your password has been updated",
  updatePasswordSuccessDescription: "You can now log in with your new password.",
  backToLogin: "Back to log in",
  noAccount: "No account yet?",
  haveAccount: "Already have an account?",
  otp: "One-time password",
  otpSubmit: "Verify",
  otpHelp: "You can find your one-time password in the Google Authenticator or Authy app.",
  continueWith: "Continue with",
  orContinueWith: "or continue with",
  verificationCode: "Your verification code",
  email: "Email",
  password: "Password",
  newPassword: "New password",
  confirmPassword: "Confirm password"
};
var tpl = (value, data) => {
  return value.replace(/{(\w*)}/g, function(m, key) {
    return Object.prototype.hasOwnProperty.call(data, key) ? data[key] : "";
  });
};
var Auth = (props) => {
  const {
    view,
    defaultView = VIEWS.LOGIN,
    onViewChange,
    providers,
    title,
    translations: translationsProp,
    signupLink,
    loginLink,
    forgotLink,
    backLink,
    onError,
    onSuccess,
    redirectUrl,
    fields,
    ...rest
  } = props;
  const translations = { ...defaultTranslations, ...translationsProp };
  const { type } = rest;
  const [authView, setAuthView] = useControllableState({
    defaultValue: defaultView,
    value: view,
    onChange: (view2) => {
      onViewChange == null ? void 0 : onViewChange(view2);
    }
  });
  const errorHandler = React3.useCallback(
    (view2) => (error) => {
      if (authView === view2 && onError) {
        onError(view2, error);
      }
    },
    [authView]
  );
  const successHandler = React3.useCallback(
    (view2) => (data) => {
      if (authView === view2 && onSuccess) {
        onSuccess(view2, data);
      }
    },
    [authView]
  );
  const getRedirectUrl = React3.useCallback(
    (view2) => {
      if (typeof redirectUrl === "function") {
        return redirectUrl(view2);
      }
      return redirectUrl;
    },
    [redirectUrl]
  );
  switch (authView) {
    case VIEWS.LOGIN:
      return /* @__PURE__ */ jsx16(
        LoginView,
        {
          providers,
          onError: errorHandler(VIEWS.LOGIN),
          onSuccess: successHandler(VIEWS.LOGIN),
          footer: /* @__PURE__ */ jsx16(
            AuthLink,
            {
              onClick: () => setAuthView(VIEWS.SIGNUP),
              label: translations.noAccount,
              link: signupLink != null ? signupLink : translations.signup
            }
          ),
          redirectUrl: getRedirectUrl(VIEWS.LOGIN),
          title: title != null ? title : translations.login,
          providerLabel: translations.continueWith,
          dividerLabel: translations.orContinueWith,
          fields: {
            ...fields,
            email: {
              label: translations.email,
              ...fields == null ? void 0 : fields.email
            },
            password: {
              label: translations.password,
              ...fields == null ? void 0 : fields.password
            },
            submit: {
              children: translations.loginSubmit,
              ...fields == null ? void 0 : fields.submit
            }
          },
          renderSuccess: type === "magiclink" ? (data) => /* @__PURE__ */ jsx16(
            AuthFormSuccess,
            {
              title: translations.magicLinkSuccess,
              description: tpl(translations.magicLinkSuccessDescription, {
                email: (data == null ? void 0 : data.email) || translations.yourEmail
              })
            }
          ) : void 0,
          ...rest,
          children: type === "password" && (!forgotLink || typeof forgotLink === "string" ? /* @__PURE__ */ jsx16(
            Link,
            {
              fontSize: "md",
              color: "muted",
              float: "right",
              onClick: () => setAuthView(VIEWS.FORGOT_PASSWORD),
              children: forgotLink != null ? forgotLink : translations.forgotPassword
            }
          ) : forgotLink)
        }
      );
    case VIEWS.SIGNUP:
      return /* @__PURE__ */ jsx16(
        SignupView,
        {
          providers,
          onError: errorHandler(VIEWS.SIGNUP),
          onSuccess: successHandler(VIEWS.SIGNUP),
          footer: /* @__PURE__ */ jsx16(
            AuthLink,
            {
              onClick: () => setAuthView(VIEWS.LOGIN),
              label: translations.haveAccount,
              link: loginLink != null ? loginLink : translations.login
            }
          ),
          redirectUrl: getRedirectUrl(VIEWS.SIGNUP),
          title: title != null ? title : translations.signup,
          providerLabel: translations.continueWith,
          dividerLabel: translations.orContinueWith,
          fields: {
            ...fields,
            email: {
              label: translations.email,
              ...fields == null ? void 0 : fields.label
            },
            password: {
              label: translations.password,
              ...fields == null ? void 0 : fields.password
            },
            submit: {
              children: translations.signupSubmit,
              ...fields == null ? void 0 : fields.submit
            }
          },
          renderSuccess: () => /* @__PURE__ */ jsx16(
            AuthFormSuccess,
            {
              title: translations.signupSuccess,
              description: translations.signupSuccessDescription
            }
          ),
          ...rest
        }
      );
    case VIEWS.FORGOT_PASSWORD:
      return /* @__PURE__ */ jsx16(
        ForgotPasswordView,
        {
          onError: errorHandler(VIEWS.FORGOT_PASSWORD),
          onSuccess: successHandler(VIEWS.FORGOT_PASSWORD),
          footer: /* @__PURE__ */ jsx16(
            AuthLink,
            {
              onClick: () => setAuthView(VIEWS.LOGIN),
              link: backLink != null ? backLink : translations.backToLogin
            }
          ),
          redirectUrl: getRedirectUrl(VIEWS.FORGOT_PASSWORD),
          title: title != null ? title : translations.forgotPassword,
          fields: {
            ...fields,
            email: {
              label: translations.email,
              ...fields == null ? void 0 : fields.email
            },
            submit: {
              children: translations.forgotPasswordSubmit,
              ...fields == null ? void 0 : fields.submit
            }
          },
          renderSuccess: () => /* @__PURE__ */ jsx16(
            AuthFormSuccess,
            {
              title: translations.forgotPasswordSuccess,
              description: translations.forgotPasswordSuccessDescription
            }
          ),
          ...rest
        }
      );
    case VIEWS.UPDATE_PASSWORD:
      return /* @__PURE__ */ jsx16(
        UpdatePasswordView,
        {
          onError: errorHandler(VIEWS.UPDATE_PASSWORD),
          onSuccess: successHandler(VIEWS.UPDATE_PASSWORD),
          title: title != null ? title : translations.updatePassword,
          fields: {
            ...fields,
            password: {
              label: translations.newPassword,
              ...fields == null ? void 0 : fields.password
            },
            confirmPassword: {
              label: translations.confirmPassword,
              ...fields == null ? void 0 : fields.confirmPassword
            },
            submit: {
              children: translations.updatePasswordSubmit,
              ...fields == null ? void 0 : fields.submit
            }
          },
          renderSuccess: () => /* @__PURE__ */ jsx16(
            AuthFormSuccess,
            {
              title: translations.updatePasswordSuccess,
              description: translations.updatePasswordSuccessDescription
            }
          ),
          ...rest
        }
      );
    case VIEWS.OTP:
      return /* @__PURE__ */ jsx16(
        OtpView,
        {
          onError: errorHandler(VIEWS.OTP),
          onSuccess: successHandler(VIEWS.OTP),
          title: title != null ? title : translations.otp,
          fields: {
            ...fields,
            otp: {
              label: translations.verificationCode,
              help: translations.otpHelp,
              ...fields == null ? void 0 : fields.otp
            },
            submit: {
              children: translations.otpSubmit,
              ...fields == null ? void 0 : fields.submit
            }
          },
          ...rest
        }
      );
  }
  return null;
};
var AuthLink = ({ label, link, onClick }) => {
  const styles = {
    textAlign: "center",
    pt: 4,
    fontSize: "md"
  };
  return /* @__PURE__ */ jsxs13(chakra2.div, { __css: styles, children: [
    label && /* @__PURE__ */ jsx16(chakra2.span, { color: "muted", children: label }),
    " ",
    typeof link === "string" ? /* @__PURE__ */ jsx16(Link, { onClick, children: link }) : link
  ] });
};
Auth.displayName = "Auth";

// src/components/authenticated.tsx
import { runIfFn } from "@chakra-ui/utils";
import React4 from "react";
var Authenticated = (props) => {
  const { children, onUnauthenticated, fallback } = props;
  const auth = useAuth();
  const { isAuthenticated, isLoading, isLoggingIn } = auth;
  const unauthenticated = !isAuthenticated && !isLoading && !isLoggingIn;
  React4.useEffect(() => {
    if (unauthenticated) {
      onUnauthenticated == null ? void 0 : onUnauthenticated();
    }
  }, [unauthenticated]);
  if (isLoading || !isLoggingIn && !isAuthenticated) {
    return runIfFn(fallback, auth);
  }
  return runIfFn(children, auth);
};
export {
  Auth,
  AuthContext,
  AuthFormContainer,
  AuthFormDivider,
  AuthFormSuccess,
  AuthFormTitle,
  AuthProvider,
  Authenticated,
  ForgotPasswordForm,
  ForgotPasswordView,
  LoginButton,
  LoginView,
  MagicLinkForm,
  OtpForm,
  OtpView,
  PasswordForm,
  ProviderButton,
  Providers,
  SignupView,
  UpdatePasswordForm,
  UpdatePasswordView,
  VIEWS,
  useAuth,
  useCurrentUser,
  useLogin,
  useOtp,
  useResetPassword,
  useSignUp,
  useUpdatePassword
};
//# sourceMappingURL=index.mjs.map